import React, { useState } from 'react';
import { Card, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import '../common.css';

// Revenue source options
const revenueSourceOptions = [
  { id: "ticket-sales", label: "Ticket sales" },
  { id: "registrations", label: "Registrations" },
  { id: "sponsorships", label: "Sponsorships" },
  { id: "merchandise", label: "Merchandise" },
  { id: "concessions", label: "Concessions" },
  { id: "workshops", label: "Workshops/add-ons" },
  { id: "digital", label: "Digital content" },
  { id: "none", label: "None (cost center)" },
  { id: "other", label: "Other" }
];

// Budget priority categories
const budgetPriorityCategories = [
  { id: "venue", label: "Venue quality" },
  { id: "technical", label: "Technical production" },
  { id: "marketing", label: "Marketing/promotion" },
  { id: "transportation", label: "Transportation/logistics" },
  { id: "accommodations", label: "Accommodations" },
  { id: "talent", label: "Talent/speakers" },
  { id: "staff", label: "Staff/personnel" },
  { id: "hospitality", label: "Hospitality" }
];

const BudgetParameters = ({ formData, onInputChange, onNext, onPrev, error }) => {
  // State for custom revenue source
  const [customRevenueSource, setCustomRevenueSource] = useState(formData.customRevenueSource || '');
  
  // Handle input changes for simple fields
  const handleInputChange = (field, value) => {
    onInputChange({ [field]: value });
  };

  // Handle currency input (ensure proper format)
  const handleCurrencyInput = (field, value) => {
    // Remove any non-numeric characters except decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');
    onInputChange({ [field]: numericValue });
  };

  // Handle checkbox changes for revenue sources
  const handleRevenueSourceChange = (e) => {
    const value = e.target.value;
    const isChecked = e.target.checked;
    
    const currentSources = formData.revenueSources || [];
    let newSources;
    
    if (isChecked) {
      newSources = [...currentSources, value];
    } else {
      newSources = currentSources.filter(source => source !== value);
      // If removing "other", also clear the custom source
      if (value === "other") {
        setCustomRevenueSource('');
      }
    }
    
    onInputChange({ revenueSources: newSources });
  };

  // Handle priority ranking change
  const handlePriorityChange = (categoryId, rank) => {
    const currentPriorities = { ...(formData.budgetPriorities || {}) };
    currentPriorities[categoryId] = parseInt(rank);
    onInputChange({ budgetPriorities: currentPriorities });
  };

  // Check if a revenue source is selected
  const isRevenueSourceSelected = (sourceId) => {
    return (formData.revenueSources || []).includes(sourceId);
  };

  // Get priority rank for a category
  const getPriorityRank = (categoryId) => {
    return formData.budgetPriorities?.[categoryId] || '';
  };

  return (
    <Card className="dark-main-card text-white mb-4">
      <Card.Header className="dark-card-header border-bottom">
        <h4 className="mb-0 text-white">Establish Your Financial Framework</h4>
      </Card.Header>
      <Card.Body className="p-4">
        {error && <Alert variant="danger" className="dark-alert mb-4">{error}</Alert>}
        
        <Form>
          {/* Overall Budget Range Section */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Overall Budget Range</h5>
            
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-white">Minimum Budget</Form.Label>
                  <div className="input-group">
                    <span className="input-group-text dark-input-group-text">$</span>
                    <Form.Control
                      type="text"
                      className="dark-input"
                      placeholder="0"
                      value={formData.budgetMin || ''}
                      onChange={(e) => handleCurrencyInput('budgetMin', e.target.value)}
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-white">Maximum Budget</Form.Label>
                  <div className="input-group">
                    <span className="input-group-text dark-input-group-text">$</span>
                    <Form.Control
                      type="text"
                      className="dark-input"
                      placeholder="0"
                      value={formData.budgetMax || ''}
                      onChange={(e) => handleCurrencyInput('budgetMax', e.target.value)}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Budget flexibility</Form.Label>
              <Form.Range
                min={0}
                max={100}
                value={formData.budgetFlexibility || 0}
                onChange={(e) => handleInputChange('budgetFlexibility', e.target.value)}
              />
              <div className="d-flex justify-content-between">
                <small className="text-light">Fixed</small>
                <small className="text-light">Flexible</small>
              </div>
            </Form.Group>
          </div>
          
          {/* Budget Allocation Priorities Section */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Budget Allocation Priorities</h5>
            <p className="text-light small mb-3">Rank in order of importance (1 = highest priority)</p>
            
            <Row>
              {budgetPriorityCategories.map((category) => (
                <Col md={6} key={category.id} className="mb-3">
                  <Form.Group>
                    <div className="d-flex align-items-center">
                      <Form.Label className="text-white mb-0 me-2" style={{ flex: 1 }}>
                        {category.label}
                      </Form.Label>
                      <Form.Control
                        type="number"
                        className="dark-input"
                        style={{ width: '60px' }}
                        min="1"
                        max={budgetPriorityCategories.length}
                        value={getPriorityRank(category.id)}
                        onChange={(e) => handlePriorityChange(category.id, e.target.value)}
                      />
                    </div>
                  </Form.Group>
                </Col>
              ))}
            </Row>
          </div>
          
          {/* Financial Targets Section */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Financial Targets</h5>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Expected revenue sources</Form.Label>
              <Row>
                {revenueSourceOptions.slice(0, revenueSourceOptions.length - 1).map((option) => (
                  <Col md={6} key={option.id} className="mb-2">
                    <Form.Check
                      type="checkbox"
                      id={`revenue-${option.id}`}
                      className="dark-checkbox"
                      label={option.label}
                      value={option.id}
                      checked={isRevenueSourceSelected(option.id)}
                      onChange={handleRevenueSourceChange}
                    />
                  </Col>
                ))}
              </Row>
              
              <Row className="mt-2">
                <Col md={6} className="mb-2">
                  <Form.Check
                    type="checkbox"
                    id="revenue-other"
                    className="dark-checkbox"
                    label="Other"
                    value="other"
                    checked={isRevenueSourceSelected("other")}
                    onChange={handleRevenueSourceChange}
                  />
                </Col>
                <Col md={6}>
                  {isRevenueSourceSelected("other") && (
                    <Form.Control
                      type="text"
                      className="dark-input"
                      placeholder="Specify other revenue source"
                      value={customRevenueSource}
                      onChange={(e) => {
                        setCustomRevenueSource(e.target.value);
                        onInputChange({ customRevenueSource: e.target.value });
                      }}
                    />
                  )}
                </Col>
              </Row>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Revenue target per location</Form.Label>
              <div className="input-group">
                <span className="input-group-text dark-input-group-text">$</span>
                <Form.Control
                  type="text"
                  className="dark-input"
                  placeholder="Enter target revenue per event location"
                  value={formData.revenueTarget || ''}
                  onChange={(e) => handleCurrencyInput('revenueTarget', e.target.value)}
                />
              </div>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Break-even requirement</Form.Label>
              <div>
                <Form.Check
                  inline
                  type="radio"
                  id="breakeven-yes"
                  className="dark-checkbox"
                  label="Yes, must break even"
                  name="breakEvenRequirement"
                  value="yes"
                  checked={formData.breakEvenRequirement === "yes"}
                  onChange={(e) => handleInputChange('breakEvenRequirement', e.target.value)}
                />
                <Form.Check
                  inline
                  type="radio"
                  id="breakeven-no"
                  className="dark-checkbox"
                  label="No, can operate at a loss"
                  name="breakEvenRequirement"
                  value="no"
                  checked={formData.breakEvenRequirement === "no"}
                  onChange={(e) => handleInputChange('breakEvenRequirement', e.target.value)}
                />
              </div>
            </Form.Group>
          </div>
          
          {/* Cost Constraints Section */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Cost Constraints</h5>
            
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label className="text-white">Maximum per-event budget</Form.Label>
                  <div className="input-group">
                    <span className="input-group-text dark-input-group-text">$</span>
                    <Form.Control
                      type="text"
                      className="dark-input"
                      placeholder="Maximum budget per event"
                      value={formData.maxPerEventBudget || ''}
                      onChange={(e) => handleCurrencyInput('maxPerEventBudget', e.target.value)}
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label className="text-white">Maximum venue cost</Form.Label>
                  <div className="input-group">
                    <span className="input-group-text dark-input-group-text">$</span>
                    <Form.Control
                      type="text"
                      className="dark-input"
                      placeholder="Maximum venue budget"
                      value={formData.maxVenueCost || ''}
                      onChange={(e) => handleCurrencyInput('maxVenueCost', e.target.value)}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Maximum per-person cost</Form.Label>
              <div className="input-group">
                <span className="input-group-text dark-input-group-text">$</span>
                <Form.Control
                  type="text"
                  className="dark-input"
                  placeholder="Maximum cost per attendee"
                  value={formData.maxPerPersonCost || ''}
                  onChange={(e) => handleCurrencyInput('maxPerPersonCost', e.target.value)}
                />
              </div>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Other cost limitations</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                className="dark-input"
                placeholder="Any other budget constraints or limitations"
                value={formData.costLimitations || ''}
                onChange={(e) => handleInputChange('costLimitations', e.target.value)}
              />
            </Form.Group>
          </div>
          
          {/* Additional Budget Notes */}
          <Form.Group className="mb-4">
            <Form.Label className="text-white">Additional Budget Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              className="dark-input"
              placeholder="Any other financial considerations or requirements"
              value={formData.additionalBudgetNotes || ''}
              onChange={(e) => handleInputChange('additionalBudgetNotes', e.target.value)}
            />
          </Form.Group>

          <div className="d-flex justify-content-between">
            <Button
              variant="outline-light"
              className="dark-outline-button"
              onClick={onPrev}
            >
              <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
              Back
            </Button>
            <Button
              className="dark-primary-button"
              onClick={onNext}
            >
              Continue
              <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default BudgetParameters;